import { useCallback, useEffect, useMemo } from 'react';

import { useQueryClient, useSuspenseQuery } from '@mint-lib/query-client';
import { AiAssistantSocket, Chat } from '@mint-sdk/api-ai-assistant';
import { useAtomValue } from 'jotai';

import { useChatHistoryState } from '../hooks/useChatHistoryState.jsx';
import { useStubMessage } from '../hooks/useStubMessage.jsx';
import { isS2SAtom } from './atoms/chat.js';
import { useChatTarget } from './ChatTargetProvider.jsx';
import { useSocket } from './SocketProvider.jsx';

type SendMessagePayload = {
  text: string;
  metadata?: Chat.UserMetadataSchemaType;
};

const SYSTEM_ERROR_STATUS_CODES = new Set([503, 500, 403, 429, 422]);

export const useTextChatActions = (chatId: string | undefined) => {
  const queryClient = useQueryClient();
  const { chatSocket, connect, getConnected, authenticate } = useSocket();
  const target = useChatTarget();

  const { data: chatList } = useSuspenseQuery({
    ...Chat.getChatList({ params: {} }),
    staleTime: Infinity,
  });

  const isS2S = useAtomValue(isS2SAtom);
  const { createStubMessage, createSystemErrorMessage } =
    useStubMessage(chatId);

  const { addNewMsgToLocalState, updateMsgSendViaSocket } =
    useChatHistoryState();

  const currentChat = useMemo(
    () => chatList.find((chat) => chat.chatSessionId === chatId),
    [chatList, chatId],
  );

  const sendMessage = useCallback(
    (payload: SendMessagePayload) => {
      if (!chatSocket) return;

      const stubMessage = createStubMessage(payload);

      if (chatId) {
        addNewMsgToLocalState(chatId, stubMessage);
      }

      if (!getConnected()) {
        connect();

        return;
      }

      const sent = chatSocket.sendChatMessage({
        chatTitle: currentChat?.chatTitle,
        userPrompt: payload.text,
        metadata: payload.metadata,
        target,
      });

      if (sent && chatId) {
        updateMsgSendViaSocket(chatId, stubMessage.messageId);
      }
    },
    [
      chatSocket,
      connect,
      getConnected,
      chatId,
      target,
      currentChat?.chatTitle,
      addNewMsgToLocalState,
      createStubMessage,
      updateMsgSendViaSocket,
    ],
  );

  const handleMessage = useCallback(
    (response: AiAssistantSocket.IncomingChatMessage) => {
      if ('detail' in response) {
        if (chatId) {
          addNewMsgToLocalState(chatId, createSystemErrorMessage());
        }
        return;
      }

      if (response.statusCode !== 201 && chatId) {
        addNewMsgToLocalState(chatId, response.message);
      }
    },
    [chatId, addNewMsgToLocalState, createSystemErrorMessage],
  );

  /** On the schema-free twin, so a rejected frame still refreshes a missing title. */
  const handleMessageFrame = useCallback(
    (data: unknown) => {
      if (typeof data !== 'object' || data === null) {
        return;
      }

      if (!currentChat?.chatTitle) {
        Chat.getChatList.invalidate(queryClient, { params: {} });
      }
    },
    [queryClient, currentChat?.chatTitle],
  );

  const handleError = useCallback(
    (error: AiAssistantSocket.ChatSocketErrorEvent) => {
      /**
       * 408 is the 5-minute idle timeout. The server answers this re-auth with
       * a DISCONNECT today, so recovery really comes from the next `connect()`;
       * the frame stays as a fallback for a server that keeps the socket open.
       */
      if (error.statusCode === 408) {
        authenticate();
        return;
      }

      if (SYSTEM_ERROR_STATUS_CODES.has(error.statusCode) && chatId) {
        addNewMsgToLocalState(chatId, createSystemErrorMessage());
      }
    },
    [chatId, addNewMsgToLocalState, createSystemErrorMessage, authenticate],
  );

  const handlePendingMessages = useCallback(() => {
    if (!chatSocket || isS2S || !chatId) return;

    /** Read through the cache, not a rendered prop: `updateMsgSendViaSocket`
     * writes synchronously, so a second handshake in the same tick sees the
     * cleared flags. */
    const lastChatMessage = queryClient
      .getQueryData(
        Chat.getHistoryById.queryKey.dataTag({
          urlParams: { chatSessionId: chatId },
        }),
      )
      ?.at(-1);

    if (lastChatMessage?.message.type !== 'human') {
      return;
    }

    const { isMsgStub, isApprovingStub, content, additionalMetadata } =
      lastChatMessage.message;

    if (!isMsgStub && !isApprovingStub) {
      return;
    }

    const sent = chatSocket.sendChatMessage({
      chatTitle: currentChat?.chatTitle ?? null,
      userPrompt: content,
      metadata: additionalMetadata?.metadata,
      target: {
        ...target,
        isOverride: !!isApprovingStub,
      },
    });

    if (sent) {
      updateMsgSendViaSocket(chatId, lastChatMessage.messageId);
    }
  }, [
    chatSocket,
    chatId,
    target,
    isS2S,
    queryClient,
    currentChat?.chatTitle,
    updateMsgSendViaSocket,
  ]);

  useEffect(() => {
    if (!chatSocket) return;

    return chatSocket.onChatMessage(handleMessage);
  }, [chatSocket, handleMessage]);

  useEffect(() => {
    if (!chatSocket) return;

    return chatSocket.onChatMessageFrame(handleMessageFrame);
  }, [chatSocket, handleMessageFrame]);

  useEffect(() => {
    if (!chatSocket) return;

    return chatSocket.onChatError(handleError);
  }, [chatSocket, handleError]);

  useEffect(() => {
    if (!chatSocket) return;

    return chatSocket.onAuthenticated(handlePendingMessages);
  }, [chatSocket, handlePendingMessages]);

  return { sendMessage };
};
